import React from "react"

import { Auth0Provider } from "@auth0/auth0-react"
import * as msal from "@azure/msal-browser"
import { MsalProvider } from "@azure/msal-react"
import { ConnectedRouter } from "connected-react-router"
import { createRoot } from "react-dom/client"
import { Provider } from "react-redux"
import { PersistGate } from "redux-persist/integration/react"

import { msalConfig } from "@config/msalConfig"
import { TopLevelProvider, useTopLevelContext } from "contexts/TopLevelContext"
import { getItem } from "@utils/localStorage"
import { SideBarState } from "utils/sideBarContext"

import App from "./App"
import configureStore, { history } from "./configureStore"
import { initialState as userInitialState } from "./reducers/userReducer"

// Dev deploy only: the data-layer shim changed the persisted shapes across iterations, so a browser
// that rehydrates an OLD persist:root / id-alias map can hang on the inbox load. Do a ONE-TIME clear
// of the stale dev state (bump DEV_SHIM_VERSION whenever the shim shapes change) BEFORE the store
// rehydrates. Self-heals stuck sessions without the user clearing site data; runs once per version.
const DEV_SHIM_VERSION = "2026-06-18-21"
if (process.env.REACT_APP_API_ENDPOINT === "/api") {
  try {
    if (window.localStorage.getItem("coowe.devShimVersion") !== DEV_SHIM_VERSION) {
      window.localStorage.removeItem("persist:root")
      window.localStorage.removeItem("coowe.devIdAlias")
      window.localStorage.setItem("coowe.devShimVersion", DEV_SHIM_VERSION)
    }
    // AI create/suggest on-off toggle: ?ai=on / ?ai=off persists the choice (read by
    // showAiFeature). Default ON when never set. Lets QJ flip the AI features without a rebuild.
    const ai = new URLSearchParams(window.location.search).get("ai")
    if (ai === "on" || ai === "off") {
      window.localStorage.setItem("coowe.aiEnabled", ai === "on" ? "true" : "false")
    }
  } catch (e) {
    /* ignore */
  }
}

// BUG-20260710-008: a cold direct load of a TeeUp URL (e.g. pasting /teeUps/{id} into a fresh
// tab) used to bounce to /inbox. Root cause: the persisted session (localStorage "userInfo") was
// only restored into redux inside a useEffect in App.js -- AFTER the first render/commit. On that
// first render PrivateRoute saw an unauthenticated state and redirected /teeUps/{id} to "/"; once
// the session then hydrated a moment later, PublicRoute saw "/" as authenticated and redirected
// again, this time to "/inbox" -- losing the original deep link. Fix: seed the store's initial
// "user" slice synchronously from localStorage BEFORE the first render, so PrivateRoute/PublicRoute
// never observe the transient unauthenticated state on a cold load. Warm in-app navigation already
// had a hydrated store and was never affected.
const persistedUserInfo = getItem("userInfo")
const preloadedState = persistedUserInfo
  ? { user: { ...userInitialState, userInfo: persistedUserInfo } }
  : undefined

export const { store, persistor } = configureStore(preloadedState)
export const domain = process.env.REACT_APP_AUTH0_DOMAIN
export const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID
export const captchaId = process.env.REACT_APP_GOOGLE_API_CAPTCHA_KEY
export const AUTH0SCOPE =
  "read:current_user update:current_user_identities update:current_user_metadata"
export const AUTH0AUDIENCE = `https://${domain}/api/v2/`
export const msalInstance = new msal.PublicClientApplication(msalConfig)

const AppWithProviders = () => {
  const { setIsRegistrationInProgress, setSocialRegistrationType } =
    useTopLevelContext()

  const onRedirectCallback = appState => {
    if (appState?.isRegistrationInProgress) {
      setIsRegistrationInProgress(appState.isRegistrationInProgress)
    }
    if (appState?.socialRegistrationType) {
      setSocialRegistrationType(appState.socialRegistrationType)
    }
  }

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      redirectUri={window.location.origin || "https://web.coowe.com"}
      audience={AUTH0AUDIENCE}
      scope={AUTH0SCOPE}
      useRefreshTokens
      onRedirectCallback={onRedirectCallback}>
      <MsalProvider instance={msalInstance}>
        <Provider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <ConnectedRouter history={history}>
              <SideBarState>
                <App />
              </SideBarState>
            </ConnectedRouter>
          </PersistGate>
        </Provider>
      </MsalProvider>
    </Auth0Provider>
  )
}

const root = createRoot(document.querySelector("#root"))

root.render(
  <TopLevelProvider>
    <AppWithProviders />
  </TopLevelProvider>
)
